import { useId, useState } from 'react';
import { MAX_INVITE_ADDRESSES, waitLabel, type AcceptRefusal, type WaitLabel } from '../../core/invitations';
import type { PendingInvitation } from '../api';
import {
  InviteResultList, planInvites, SKIPPED_COPY, type InviteResult, type SkippedAddress,
} from './InviteResults';
import type { FetchState } from './MaterialList';
import { plural } from './plural';

/**
 * Bringing colleagues in (R1, R2, R7, R8), several at a time.
 *
 * An admin pastes a list — a column from a spreadsheet, a line from a mail client — and gets
 * one answer per address. Each link is the whole credential and it is shown once, here, in
 * plaintext, unless Collab emailed it. Three consequences are written into this component
 * rather than left to a reader's memory:
 *
 * - **`collab-invite-dialog` is a replay block selector.** This panel holds addresses and live
 *   tokens at the same time; a recording of it is a recording of working invitations.
 * - **What the paste will do is shown before it does it.** Addresses that are not addresses,
 *   the admin's own, repeats and anything past the cap are listed as skipped while the admin
 *   is still typing, by the same `core` rule the Worker applies. Nobody waits on twenty
 *   requests to learn the fourth line had a typo.
 * - **What a link does is stated next to it.** That is `InviteLink`, shared with the
 *   new-group flow, because a second rendering of a live link is where one of the three facts
 *   goes missing.
 *
 * The pending list is the other half. Every refused accept tells the invitee one
 * indistinguishable thing (R12), so the admin who typed the address is the only person who can
 * see that a link bounced — and the wait label is what tells them to do something about it.
 * The label is decided in `core` (`waitLabel`, KTD12); this file only chooses its words.
 */

export interface InviteDialogProps {
  pending: PendingInvitation[];
  pendingState: FetchState;
  /** One row per address from the latest paste or resend, in the order they were typed. */
  results: InviteResult[];
  /** ISO time the wait labels are measured against. */
  now: string;
  /** The admin's own address, when known, so inviting themselves is skipped up front. */
  selfEmail: string | null;
  /** A batch, a resend or a revoke is in flight. */
  busy: boolean;
  error: string | null;
  onInvite: (emails: string[], skipped: SkippedAddress[]) => void;
  onRetryAddress: (email: string) => void;
  onResend: (invitation: PendingInvitation) => void;
  onRevoke: (invitation: PendingInvitation) => void;
  onRetry: () => void;
  onClose: () => void;
}

/** What the admin is told about a link that bounced. Never shown to the invitee (R10, R12). */
const REFUSAL_COPY: Readonly<Record<AcceptRefusal, string>> = {
  wrong_recipient: 'Opened by a different address. Check the spelling and invite again.',
  unverified_address: 'Opened by an account with no verified address.',
  expired: 'This link has passed its seven days.',
  revoked: 'This link was revoked.',
  consumed: 'This link has already been used.',
  declined: 'They declined this invitation.',
  // The Worker records `cap_reached` (`core/invitations.ts`, `AcceptRefusal`). This key was
  // `group_full` until the refusal was looked up against what is actually written.
  cap_reached: 'The group was full when they tried.',
  inviter_not_admin: 'The admin who created this link is no longer an admin here.',
  unknown_token: 'A link that matched nothing was tried.',
};

const DAY_MS = 24 * 60 * 60 * 1000;

const daysSince = (from: string, now: string): number =>
  Math.max(0, Math.floor((Date.parse(now) - Date.parse(from)) / DAY_MS));

/** A wait label without its refusal reason: `refused:cap_reached` is the `refused` kind. */
const waitKind = (label: WaitLabel): string => (label.startsWith('refused:') ? 'refused' : label);

/** The words for a wait label. The label itself — and its precedence — is `core`'s. */
function waitCopy(label: WaitLabel, invitation: PendingInvitation, now: string): string {
  if (label === 'declined') {
    return `Declined on ${new Date(invitation.declinedAt ?? invitation.createdAt).toLocaleDateString()}`;
  }
  if (label === 'expired') return 'Expired — send another';
  if (waitKind(label) === 'refused') {
    return REFUSAL_COPY[label.slice('refused:'.length) as AcceptRefusal] ?? 'The last attempt on this link was refused.';
  }
  const days = daysSince(invitation.createdAt, now);
  if (label === 'waiting') return `Waiting ${plural(days, 'day')} — check the address`;
  return days === 0 ? 'Invited today' : `Invited ${plural(days, 'day')} ago`;
}

export default function InviteDialog({
  pending, pendingState, results, now, selfEmail, busy, error,
  onInvite, onRetryAddress, onResend, onRevoke, onRetry, onClose,
}: InviteDialogProps) {
  const [draft, setDraft] = useState('');
  /** The declined row whose Resend is waiting on "Send anyway". One at a time. */
  const [confirming, setConfirming] = useState<string | null>(null);
  const listId = useId();

  const plan = planInvites(draft, selfEmail);

  return (
    <section className="panel collab-invite-dialog" aria-label="Invite a colleague">
      <h3>Invite colleagues</h3>

      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (busy || plan.emails.length === 0) return;
          onInvite(plan.emails, plan.skipped);
          setDraft('');
        }}
      >
        <label htmlFor={listId}>Their email addresses</label>
        <p className="hint" id={`${listId}-hint`}>
          Paste or type up to {MAX_INVITE_ADDRESSES} — one per line, or separated by commas or spaces.
        </p>
        <textarea
          id={listId}
          rows={3}
          value={draft}
          aria-describedby={`${listId}-hint`}
          onChange={(event) => setDraft(event.target.value)}
          placeholder="colleague@240tutoring.com"
          disabled={busy}
        />

        {/* Before anything is sent: what this will do, and what it will leave out (AE1). */}
        <div aria-live="polite">
          {plan.emails.length > 0 && (
            <p className="meta">{plural(plan.emails.length, 'address', 'addresses')} to invite.</p>
          )}
          {plan.skipped.length > 0 && (
            <ul className="address-list" aria-label="Skipped addresses">
              {plan.skipped.map((entry, index) => (
                <li key={`${index}:${entry.email}`}>
                  <span>{entry.email}</span>
                  <p className="hint">{SKIPPED_COPY[entry.reason]}</p>
                </li>
              ))}
            </ul>
          )}
        </div>

        <button type="submit" disabled={busy || plan.emails.length === 0}>
          Create invitations
        </button>
      </form>

      {error && <p className="error" role="alert">{error}</p>}

      <div aria-live="polite">
        <InviteResultList results={results} onRetry={onRetryAddress} busy={busy} />
      </div>

      <h4>Pending invitations</h4>

      {pendingState === 'loading' && <p className="hint" role="status">Loading invitations…</p>}

      {pendingState === 'failed' && (
        <div role="alert">
          <p className="error">We could not load the pending invitations.</p>
          <button type="button" onClick={onRetry}>Try again</button>
        </div>
      )}

      {pendingState === 'ready' && pending.length === 0 && (
        <p className="hint" role="status">No invitations are waiting.</p>
      )}

      {pendingState === 'ready' && pending.length > 0 && (
        <ul className="address-list pending-invitations">
          {pending.map((invitation) => {
            const label = waitLabel(invitation, now);
            // Revoking an expired or declined link does nothing: it already does not work.
            const revocable = label !== 'expired' && label !== 'declined';
            return (
              <li key={invitation.id} className={`wait-${waitKind(label)}`}>
                <span>{invitation.email}</span>
                {/*
                  * Visible words are a prefix of each accessible name, which carries the address:
                  * a list of twenty pending rows is twenty Resend buttons to a screen reader.
                  */}
                <span className="row-actions">
                  <button
                    type="button"
                    disabled={busy}
                    aria-label={`Resend to ${invitation.email}`}
                    onClick={() => {
                      // A decline is the colleague saying no. Asking them again takes one
                      // deliberate second step — inline, never `window.confirm`.
                      if (label === 'declined') setConfirming(invitation.id);
                      else onResend(invitation);
                    }}
                  >
                    Resend
                  </button>
                  {revocable && (
                    <button
                      type="button"
                      className="secondary"
                      disabled={busy}
                      aria-label={`Revoke invitation for ${invitation.email}`}
                      onClick={() => onRevoke(invitation)}
                    >
                      Revoke
                    </button>
                  )}
                </span>
                <p className="hint">{waitCopy(label, invitation, now)}</p>
                {confirming === invitation.id && (
                  <div className="inline-confirm" role="group" aria-label={`Resend to ${invitation.email}?`}>
                    <p>They declined this one. Send a new invitation anyway?</p>
                    <button
                      type="button"
                      disabled={busy}
                      aria-label={`Send anyway to ${invitation.email}`}
                      onClick={() => { setConfirming(null); onResend(invitation); }}
                    >
                      Send anyway
                    </button>
                    <button
                      type="button"
                      className="secondary"
                      aria-label={`Cancel resending to ${invitation.email}`}
                      onClick={() => setConfirming(null)}
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <button type="button" onClick={onClose}>Done</button>
    </section>
  );
}
